import { fymAPI, useGetReleaseKitQuery } from '../../../../../redux/api/fymAPI';
import {
  GetReleaseKitResponse,
  KIT_ASSET_WORKING_STATUSES,
  SerializedKitAsset,
} from '../../../../../redux/api/fymAPI.types';
import { SerializedReel } from '../../types';
import { useRealtimePollInterval } from '../../../../realtime/useRealtimePollInterval';

const WORKING_REEL_STATUSES: ReadonlyArray<string> = ['queued', 'analysing', 'planning', 'rendering'];

/** True while an asset or a reel of the song is queued, planning or rendering (polling, m5). */
export function isAnythingWorking(assets: SerializedKitAsset[], reels: SerializedReel[]): boolean {
  return (
    assets.some((asset) => KIT_ASSET_WORKING_STATUSES.includes(asset.status)) ||
    reels.some((reel) => WORKING_REEL_STATUSES.includes(reel.status))
  );
}

/** The song page data is read again every 5 s while something works (design m5). */
export const KIT_POLL_MS = 5000;

interface UseKitPollProps {
  accountId: number;
  catalogItemUuid: string;
  /** The answer the song page holds (the same cache entry); used until this hook has its own. */
  release: GetReleaseKitResponse;
}

/**
 * Reads the song page data and polls it while an asset or a reel of the song is queued,
 * planning or rendering. The poll reads its own newest answer from the cache, so it stops when
 * nothing works. RTK Query stops it on unmount. While the realtime socket is connected, the
 * `kit.asset.updated` and `reel.updated` events read the data again and the hook does not poll.
 */
export function useKitPoll({ accountId, catalogItemUuid, release }: UseKitPollProps): GetReleaseKitResponse {
  const { data } = fymAPI.endpoints.getReleaseKit.useQueryState({ accountId, catalogItemUuid });
  const current = data ?? release;
  const isWorking = isAnythingWorking(current.assets, current.reels);
  const pollingInterval = useRealtimePollInterval(isWorking ? KIT_POLL_MS : 0);
  useGetReleaseKitQuery({ accountId, catalogItemUuid }, { pollingInterval });
  return current;
}
